import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  Box,
  Paper,
  Divider,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import dayjs from 'dayjs';

// What a refund entry still pays out. A booking change can apply part of a refund to a charge
// the same change creates (a group-size surcharge that got more expensive per head); `amount`
// then carries the remainder, which is no longer a whole number of line item units.
function refundPayoutAmount(refund, unitPrice) {
  // `null` is checked explicitly because Number(null) is 0, which would show nothing paid out.
  const amount = refund?.amount;
  if (amount !== undefined && amount !== null && Number.isFinite(Number(amount))) return Number(amount);
  return Number(refund?.count || 0) * Number(unitPrice || 0);
}

// The part of a refund entry that was settled against new charges instead of being paid out.
function refundOffsetAmount(refund, unitPrice) {
  const full = Number(refund?.count || 0) * Number(unitPrice || 0);
  return Math.max(0, Math.round((full - refundPayoutAmount(refund, unitPrice)) * 100) / 100);
}

function LineItemPaymentModal({ open, onClose, lineItem }) {
  const getTypeLabel = type => {
    const typeMap = {
      booking: 'Buchung',
      package: 'Paket',
      upsell: 'Zusatzleistung',
      surcharge: 'Aufschlag',
    };
    return typeMap[type] || type;
  };

  const getStatusIcon = payment => {
    if (payment.count === 0) {
      return (
        <Tooltip title='Vollständig zurückerstattet'>
          <UndoIcon color='warning' />
        </Tooltip>
      );
    }
    if (payment.isPaid) {
      return (
        <Tooltip title='Bezahlt'>
          <CheckCircleIcon color='success' />
        </Tooltip>
      );
    }
    return (
      <Tooltip title='Ausstehend'>
        <CancelIcon color='error' />
      </Tooltip>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6'>
            Zahlungsverlauf für Position <b>{lineItem?.description}</b>
          </Typography>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle1' gutterBottom>
            Details
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Typography variant='body1'>
              <strong>Typ:</strong> {getTypeLabel(lineItem?.type)}
            </Typography>
            <Typography variant='body1'>
              <strong>Aktuelle Anzahl:</strong> {lineItem?.count}
            </Typography>
            <Typography variant='body1'>
              <strong>Preis pro Einheit:</strong> {lineItem?.price?.toFixed(2).replace('.', ',')} €
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant='body1'>
              <strong>Gesamtpreis:</strong>{' '}
              {(lineItem?.count * lineItem?.price)?.toFixed(2).replace('.', ',')} €
            </Typography>
          </Paper>
        </Box>

        <Typography variant='subtitle1' gutterBottom>
          Zahlungen
        </Typography>
        {lineItem?.paymentReferences?.map(payment => (
          <Paper key={payment?._id} sx={{ mb: 2, p: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <Typography variant='subtitle2'>
                  Zahlung vom {dayjs(payment?.createdAt).format('DD.MM.YYYY HH:mm')}
                </Typography>
                {getStatusIcon(payment)}
              </Box>
              <Box sx={{ pl: 2 }}>
                <Typography variant='body2'>
                  <strong>Status:</strong> {payment?.isPaid ? 'Bezahlt' : 'Ausstehend'}
                </Typography>
                <Typography variant='body2'>
                  <strong>Zahlungsart:</strong> {payment?.paymentType}
                </Typography>
                <Typography variant='body2'>
                  <strong>
                    {payment?.countRefunded > 0 ? 'Ursprüngliche Anzahl:' : 'Anzahl:'}
                  </strong>{' '}
                  {payment?.count + payment?.countRefunded}
                </Typography>
                <Typography variant='body2'>
                  <strong>
                    {payment?.countRefunded > 0 ? 'Ursprünglicher Betrag:' : 'Betrag:'}
                  </strong>{' '}
                  {((payment?.count + payment?.countRefunded) * lineItem?.price)
                    ?.toFixed(2)
                    .replace('.', ',')}{' '}
                  €
                </Typography>
                {payment?.countRefunded > 0 && (
                  <Typography variant='body2' color='error'>
                    <strong>Zurückerstattete Anzahl:</strong> {payment?.countRefunded}
                  </Typography>
                )}
                {payment?.countRefunded > 0 && (
                  <Typography variant='body2' color='error'>
                    <strong>Zurückerstatteter Betrag:</strong>{' '}
                    {(payment?.countRefunded * lineItem?.price)?.toFixed(2).replace('.', ',')} €
                  </Typography>
                )}
                {payment?.countRefunded > 0 && (
                  <Typography variant='body2' color='green'>
                    <strong>Betrag nach Rückerstattung:</strong>{' '}
                    {(payment?.count * lineItem?.price)?.toFixed(2).replace('.', ',')} €
                  </Typography>
                )}
              </Box>
            </Box>

            {payment?.refunds?.length > 0 && (
              <Box sx={{ pl: 2, borderLeft: '2px solid #e0e0e0' }}>
                <Typography variant='subtitle2' gutterBottom>
                  Erstattungen
                </Typography>
                {payment.refunds.map(refund => {
                  const refundStatus = refund?.settledByOffset
                    ? 'Verrechnet'
                    : refund?.isRefunded
                      ? 'Erstattet'
                      : 'In Bearbeitung';
                  const offset = refundOffsetAmount(refund, lineItem?.price);
                  return (
                    <Paper key={refund?._id} sx={{ p: 2, mb: 1, bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                      <Box display='flex' justifyContent='space-between' alignItems='center'>
                        <Typography variant='body2'>
                          <strong>Datum:</strong>{' '}
                          {dayjs(refund?.createdAt).format('DD.MM.YYYY HH:mm')}
                        </Typography>
                        <Tooltip title={refundStatus}>
                          {refund?.isRefunded ? (
                            <CheckCircleIcon color='success' />
                          ) : (
                            <CancelIcon color='error' />
                          )}
                        </Tooltip>
                      </Box>
                      <Typography variant='body2'>
                        <strong>Status:</strong> {refundStatus}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Anzahl:</strong> {refund?.count}
                      </Typography>
                      <Typography variant='body2'>
                        <strong>Betrag:</strong> -
                        {refundPayoutAmount(refund, lineItem?.price)
                          ?.toFixed(2)
                          .replace('.', ',')}{' '}
                        €
                      </Typography>
                      {offset > 0 && (
                        <Typography variant='body2' color='text.secondary'>
                          Davon mit neuen Positionen dieser Änderung verrechnet:{' '}
                          {offset.toFixed(2).replace('.', ',')} €
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            )}
          </Paper>
        ))}
        {(!lineItem?.paymentReferences || lineItem.paymentReferences.length === 0) && (
          <Typography variant='body1' align='center' sx={{ py: 2 }}>
            Keine Zahlungen vorhanden
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}

export default LineItemPaymentModal;
